import {
  EyeIcon,
  EyeSlashIcon,
  MagnifyingGlassIcon,
  PencilSimpleIcon,
  PlayCircleIcon,
  PlusIcon,
  TrashIcon,
} from '@phosphor-icons/react'
import { endOfDay } from 'date-fns'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useDeleteAnnouncement } from '@/api/communication/announcements/delete-announcement'
import { getCommunicationDetails } from '@/api/communication/get-communication-details'
import {
  CommunicationHistoryItem,
  getCommunicationHistoryQueryKey,
  useGetCommunicationHistory,
} from '@/api/communication/get-communication-history'
import { useGetMailingList } from '@/api/mailing-list/get-mailing-list'
import Dashboard from '@/components/Dashboard'
import { Page } from '@/components/layout/page'
import { PageHeader } from '@/components/layout/page-header'
import { TableGroup } from '@/components/layout/table-group'
import AnnouncementsAnalytics from '@/components/Organisation/CommunicationPortal/AnnoucementAnalytics/AnnoucementsAnalytics'
import { Button } from '@/components/ui/button'
import { DatePickerInput } from '@/components/ui/date-picker-input'
import { Icon } from '@/components/ui/icon'
import { Select } from '@/components/ui/select'
import { StatusTabOption, StatusTabs } from '@/components/ui/status-tabs'
import { RowAction } from '@/components/ui/table'
import { TextInput } from '@/components/ui/text-input'
import { DEFAULT_TZ } from '@/constants/date'
import { popupFactory, routerFactory, toastFactory } from '@/factories'
import { useCaptureEvent, useFilters, useLocalStorage, useSearchParams } from '@/hooks'
import { CommunicationStatusEnum, CommunicationType, CommunicationTypeEnum } from '@/models'
import { DateRange } from '@/models/helpers'
import { NewMessageFormModal } from '@/pages/communication/announcement-details/new-message-form-modal'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { resetQueries } from '@/utils'
import { getApiErrorMessage } from '@/utils/error'

import {
  ANNOUNCEMENTS_ANALYTICS_STORAGE_KEY,
  ANNOUNCEMENTS_ID,
  getAnnouncementsTableColumns,
  getChannelOptions,
} from './announcements.constants'
import * as S from './announcements.page.styled'
import {
  AnnouncementStatusFilter,
  AnnouncementStatusFilterEnum,
  announcementsTableFiltersSchema,
  announcementsTableInitialFilters,
} from './announcements.types'
import { getStatusColor } from './announcements.utils'
import { DeleteAnnouncementConfirmationPopup } from './delete-announcement-confirmation-popup'
import { AnnouncementsNewMessageTypeModal } from './new-message-type-modal'
import { AnnouncementsVideoTutorialModal } from './video-tutorial-modal'

export const AnnouncementsPage = () => {
  const { t } = useTranslation(['communication'])
  const { navigate } = routerFactory.useRouter()

  const { captureEvent, captureDebouncedEvent } = useCaptureEvent()

  const [isAnalyticsOpen, setIsAnalyticsOpen] = useLocalStorage(
    ANNOUNCEMENTS_ANALYTICS_STORAGE_KEY,
    true,
  )

  const activeBuilding = useSelector(selectActiveBuilding)
  const buildingId = activeBuilding?.id
  const timezone = activeBuilding?.timezone ?? DEFAULT_TZ

  const searchParams = useSearchParams({
    initialState: announcementsTableInitialFilters,
    schema: announcementsTableFiltersSchema,
  })

  const tableFilters = useFilters({
    controlledState: searchParams.parsedQuery,
    initialState: announcementsTableInitialFilters,
    onFiltersChange: searchParams.writeParams,
  })

  const filters = tableFilters.filters

  const queryParams = {
    buildingId,
    fromDate: filters.fromDate,
    limit: filters.limit,
    query: filters.search?.trim() || undefined,
    skip: filters.skip,
    toDate: filters.toDate,
    ...(filters.status &&
      filters.status !== AnnouncementStatusFilterEnum.enum.All && {
        status: filters.status,
      }),
    ...(filters.type?.length ? { type: filters.type } : {}),
  }

  const { data, isFetching } = useGetCommunicationHistory(queryParams)
  const { data: mailingLists = [] } = useGetMailingList({ buildingId })
  const { mutateAsync: deleteAnnouncement } = useDeleteAnnouncement()

  const mailingListOptions = useMemo(
    () => mailingLists.map((list) => ({ ...list, label: list.value })),
    [mailingLists],
  )

  const smsFeatureEnabled = !!activeBuilding?.communicateSMSAvailable
  const shouldShowActivateSmsPanel = !smsFeatureEnabled || !activeBuilding?.announcementPhone

  const statusTabOptions: StatusTabOption<AnnouncementStatusFilter>[] = useMemo(() => {
    return [
      {
        count:
          (data?.countScheduled ?? 0) + (data?.countCompleted ?? 0) + (data?.countOngoing ?? 0),
        label: t('communication:announcements.list.status.all'),
        value: AnnouncementStatusFilterEnum.enum.All,
      },
      {
        color: getStatusColor(AnnouncementStatusFilterEnum.enum.Scheduled),
        count: data?.countScheduled ?? 0,
        label: t('communication:announcements.list.status.scheduled'),
        value: AnnouncementStatusFilterEnum.enum.Scheduled,
      },
      {
        color: getStatusColor(AnnouncementStatusFilterEnum.enum.Completed),
        count: data?.countCompleted ?? 0,
        label: t('communication:announcements.list.status.completed'),
        value: AnnouncementStatusFilterEnum.enum.Completed,
      },
    ]
  }, [data, t])

  const fromDateFilter = tableFilters.getFilter('fromDate')
  const toDateFilter = tableFilters.getFilter('toDate')
  const dateRange: DateRange = [
    fromDateFilter ? new Date(fromDateFilter) : null,
    toDateFilter ? new Date(toDateFilter) : null,
  ]

  const handleTypeChange = (type: CommunicationType[]) => {
    tableFilters.setFilter('type', type)
    captureEvent('user_action', {
      name: 'type_change',
      screen: ANNOUNCEMENTS_ID,
      value: type,
    })
  }

  const handleDateChange = (range: DateRange) => {
    const fromDate = range[0]?.getTime()
    const toDate = range[1] ? endOfDay(range[1]).getTime() : undefined
    tableFilters.setFilters({ fromDate, toDate })
    captureEvent('user_action', {
      fromDate,
      name: 'time_date_range_change',
      screen: ANNOUNCEMENTS_ID,
      toDate,
    })
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    tableFilters.setFilter('search', e.target.value)
    captureDebouncedEvent('user_action', {
      name: 'search_change',
      screen: ANNOUNCEMENTS_ID,
      value: e.target.value,
    })
  }

  const handleStatusChange = (value: AnnouncementStatusFilter) => {
    tableFilters.setFilter('status', value)
    captureEvent('user_action', {
      name: 'status_change',
      screen: ANNOUNCEMENTS_ID,
      value,
    })
  }

  const columns = useMemo(
    () => getAnnouncementsTableColumns(t, timezone, mailingLists),
    [t, timezone, mailingLists],
  )

  const handleRefreshTable = () => {
    resetQueries(getCommunicationHistoryQueryKey(queryParams))
  }

  const handleWatchTutorial = () => popupFactory.open(AnnouncementsVideoTutorialModal, {})

  const handleCreateNew = () => {
    if (!activeBuilding) return

    popupFactory.open(AnnouncementsNewMessageTypeModal, {
      communicateSMSAvailable: true,
      fetchAnnouncementDetails: handleRefreshTable,
      mailingListOptions,
      refreshTable: handleRefreshTable,
      selectedBuilding: activeBuilding,
      shouldShowActivateSmsPanel,
    })
  }

  const rowActions: RowAction<CommunicationHistoryItem>[] = useMemo(
    () => [
      {
        icon: EyeIcon,
        label: t('communication:announcements.list.rowActions.details'),
        onClick: (item) => {
          navigate({
            params: { id: item.id },
            path: `/dashboard/announcements/:id/${item.type.toLowerCase() as Lowercase<CommunicationType>}`,
          })
        },
        primary: true,
      },
      {
        hidden: (item) => item.status === CommunicationStatusEnum.enum.Completed,
        icon: PencilSimpleIcon,
        label: t('communication:announcements.list.rowActions.edit'),
        onClick: async (item) => {
          if (!activeBuilding) return

          try {
            // TODO: Add loading state indication for dropdown menu item
            const details = await getCommunicationDetails({ id: item.id })
            popupFactory.open(NewMessageFormModal, {
              fetchAnnouncementDetails: handleRefreshTable,
              mailingListOptions,
              previewData: {
                ...item,
                message: details.message,
                standaloneClients: details.recipientTypes?.clients ?? [],
              },
              refreshTable: handleRefreshTable,
              selectedBuilding: activeBuilding,
              type: item.type,
            })
          } catch (error) {
            toastFactory.error(getApiErrorMessage(error))
          }
        },
      },
      {
        hidden: (item) =>
          item.type === CommunicationTypeEnum.enum.SMS ||
          item.status === CommunicationStatusEnum.enum.Completed,
        icon: TrashIcon,
        label: t('communication:announcements.list.rowActions.delete'),
        onClick: (item) => {
          popupFactory.open(DeleteAnnouncementConfirmationPopup, {
            announcementTitle: item.title,
            onConfirm: async () => {
              try {
                await deleteAnnouncement({ id: item.id, type: item.type })
                toastFactory.success(
                  t('communication:announcements.list.deleteSuccess', { title: item.title }),
                )
                handleRefreshTable()
              } catch (error) {
                toastFactory.error(getApiErrorMessage(error))
              }
            },
          })
        },
        variant: 'danger',
      },
    ],
    [navigate, mailingListOptions, activeBuilding, deleteAnnouncement, t],
  )

  return (
    // TODO: Remove Dashboard.Container from here. It's added because announcements is outside of /building-dashboard routes. Padding and background color has to be provided to match rest of the pages.
    <Dashboard.Container className="ctp-announcements__wrapper">
      <Page>
        <PageHeader
          title={t('communication:announcements.list.title')}
          titleExtra={
            <>
              <Button size="small" use="upsell-secondary" onClick={handleWatchTutorial}>
                <Icon icon={PlayCircleIcon} />
                {t('communication:announcements.list.watchTutorial')}
              </Button>
              <Button
                size="small"
                use="secondary"
                onClick={() => setIsAnalyticsOpen(!isAnalyticsOpen)}
              >
                <Icon icon={isAnalyticsOpen ? EyeSlashIcon : EyeIcon} />
                {isAnalyticsOpen
                  ? t('communication:announcements.list.hideAnalytics')
                  : t('communication:announcements.list.showAnalytics')}
              </Button>
            </>
          }
          actions={
            <>
              <Button size="small" use="primary" onClick={handleCreateNew}>
                {t('communication:announcements.list.createNew')}
                <Icon icon={PlusIcon} />
              </Button>
            </>
          }
        />

        {/* TODO: refactor AnnouncementsAnalytics */}
        <S.AnalyticsCollapse in={isAnalyticsOpen}>
          <AnnouncementsAnalytics
            isOpen={isAnalyticsOpen}
            mailingListOptions={mailingListOptions}
            messageTypeCount={(tableFilters.getFilter('type') ?? []).length}
            selectedBuilding={activeBuilding}
            shouldShowActivateSmsPanel={shouldShowActivateSmsPanel}
          />
        </S.AnalyticsCollapse>

        <TableGroup
          columns={columns}
          data={data?.history ?? []}
          emptyNoFilters={{
            description: t('communication:announcements.list.empty.noFilters.description'),
            title: t('communication:announcements.list.empty.noFilters.title'),
          }}
          emptyWithFilters={{
            description: t('communication:announcements.list.empty.withFilters.description'),
            title: t('communication:announcements.list.empty.withFilters.title'),
          }}
          getRowKey={(item) => item.id}
          id={ANNOUNCEMENTS_ID}
          loading={isFetching}
          filters={
            <Select
              options={getChannelOptions(t)}
              placeholder={t('communication:announcements.list.channel.placeholder')}
              size="small"
              type="multi"
              value={tableFilters.getFilter('type') ?? []}
              onChange={handleTypeChange}
            />
          }
          navigation={{
            datePicker: (
              <DatePickerInput
                mode="range"
                placeholder={t('communication:announcements.list.dateRange.placeholder')}
                size="small"
                timezone={timezone}
                value={dateRange}
                onRangeChange={handleDateChange}
              />
            ),
            search: (
              <TextInput
                clearable
                icon={MagnifyingGlassIcon}
                name="search"
                placeholder={t('communication:announcements.list.search.placeholder')}
                size="small"
                value={tableFilters.getFilter('search')}
                onChange={handleSearchChange}
              />
            ),
            statusTabs: (
              <StatusTabs
                options={statusTabOptions}
                value={tableFilters.getFilter('status') ?? null}
                onChange={handleStatusChange}
              />
            ),
          }}
          pagination={{
            paginationFilters: tableFilters,
            total: data?.count ?? 0,
          }}
          rowActions={rowActions}
          tableFilters={tableFilters}
        />
      </Page>
    </Dashboard.Container>
  )
}
